import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useFetchData } from '@/api/apiHooks';
import { ROUTES } from '@/constants';
import { useNavigationContext } from '@/context/NavigationContext';
import { CompaniesResponseItem, DropDownItem } from '@/utils/types';
import { URLS } from '@/utils/urls';

interface UseCompanyPanelSelectorParams {
  currentCompanyId?: string;
  currentPanelId?: string;
  currentPanelName?: string;
  onNavigate?: (companyId: string, panelId: string) => void;
  basePath: string;
}

export interface SelectOption {
  label: string;
  value: string;
  totalPanels?: number;
}

export interface PanelSelectorProps {
  displayLabel: string;
  activePanelId: string;
  panelsReady: boolean;
  panelsListOptions: SelectOption[];
  onPanelSelectedChange: (selected: string[]) => void;
}

export interface CompanySelectorProps extends PanelSelectorProps {
  companiesListOptions: SelectOption[];
  activeCompanyId: string;
  onCompanyMouseEnter: (companyId: string) => void;
  onCompanyPanelSelectedChange: (
    companyId: string
  ) => (selected: string[]) => void;
}

export const useCompanyPanelSelector = ({
  currentCompanyId,
  currentPanelId,
  currentPanelName,
  onNavigate,
  basePath,
}: UseCompanyPanelSelectorParams) => {
  const navigate = useNavigate();
  const {
    panelId: storedPanelId,
    companyId: storedCompanyId,
    setLastCompanyId,
    setPanelContext,
  } = useNavigationContext();

  const initialCompanyId = currentCompanyId ?? storedCompanyId ?? '';
  const initialPanelId = currentPanelId ?? storedPanelId ?? '';

  const [selectedCompanyId, setSelectedCompanyId] = useState(initialCompanyId);
  const [selectedPanelId, setSelectedPanelId] = useState(initialPanelId);
  const [expandedCompanyId, setExpandedCompanyId] = useState(initialCompanyId);
  const [pendingCompanyId, setPendingCompanyId] = useState<string | null>(null);

  const activeCompanyId = selectedCompanyId;
  const activePanelId = selectedPanelId;

  const { data: allCompaniesData, isLoading: isCompaniesLoading } =
    useFetchData<{ data: CompaniesResponseItem[] }>({
      url: URLS.getCompanies(),
      staleTime: 10 * 60 * 60 * 1000,
    });

  const companiesListOptions = useMemo(
    () =>
      allCompaniesData?.data?.map(({ name, id, total_panels }) => ({
        label: name,
        value: id,
        totalPanels: total_panels,
      })) ?? [],
    [allCompaniesData]
  );

  const isSingleCompany = companiesListOptions.length === 1;
  const singleCompanyId = isSingleCompany ? companiesListOptions[0].value : '';

  useEffect(() => {
    if (isSingleCompany && expandedCompanyId !== singleCompanyId) {
      setExpandedCompanyId(singleCompanyId);
      setSelectedCompanyId(singleCompanyId);
      setLastCompanyId(singleCompanyId);
    }
  }, [isSingleCompany, singleCompanyId, expandedCompanyId]);

  const {
    data: panelsData,
    isLoading: isPanelsLoading,
    isFetching: isPanelsFetching,
  } = useFetchData<DropDownItem[]>({
    url: URLS.getAllCompanyPanels(expandedCompanyId),
    enabled: !!expandedCompanyId,
    staleTime: 5 * 60 * 1000,
    keepPreviousData: false,
  });

  const panelsReady = !isPanelsLoading && !isPanelsFetching;

  const panelsListOptions = useMemo(
    () =>
      (panelsData as DropDownItem[] | undefined)?.map(({ value, key }) => ({
        label: value ?? '',
        value: key ?? '',
      })) ?? [],
    [panelsData]
  );

  const displayLabel = useMemo(() => {
    if (!activePanelId) return 'Select Panel';
    if (currentPanelName) return currentPanelName;
    return (
      panelsListOptions.find(({ value }) => value === activePanelId)?.label ??
      'Select Panel'
    );
  }, [activePanelId, currentPanelName, panelsListOptions]);

  useEffect(() => {
    if (
      pendingCompanyId &&
      panelsReady &&
      panelsListOptions.length > 0 &&
      expandedCompanyId === pendingCompanyId
    ) {
      const firstPanel = panelsListOptions[0];
      handlePanelSelect(firstPanel.value, pendingCompanyId, firstPanel.label);
      setPendingCompanyId(null);
    }
  }, [pendingCompanyId, panelsReady, panelsListOptions, expandedCompanyId]);

  const handlePanelSelect = (
    panelId: string,
    companyId: string,
    panelName?: string
  ) => {
    setSelectedCompanyId(companyId);
    setSelectedPanelId(panelId);
    setPanelContext(panelId, companyId);

    if (onNavigate) {
      onNavigate(companyId, panelId);
      return;
    }

    const navigationPath = basePath
      ? `${ROUTES.companies}/${companyId}${ROUTES.panels}/${panelId}${basePath}`
      : `${ROUTES.companies}/${companyId}${ROUTES.panels}/${panelId}/settings`;

    navigate(navigationPath, {
      state: {
        panelName,
        companyName: companiesListOptions.find(
          ({ value }) => value === companyId
        )?.label,
      },
    });
  };

  const findAndSelectPanel =
    (companyId: string) =>
    ([selected]: string[]) => {
      if (!selected) return;
      const panel = panelsListOptions.find(({ value }) => value === selected);
      handlePanelSelect(selected, companyId, panel?.label);
    };

  const handleCompanyMouseEnter = (companyId: string) => {
    if (expandedCompanyId !== companyId) {
      setExpandedCompanyId(companyId);
    }
  };

  const panelSelectorProps: PanelSelectorProps = {
    displayLabel,
    activePanelId,
    panelsReady,
    panelsListOptions,
    onPanelSelectedChange: findAndSelectPanel(singleCompanyId),
  };

  const companySelectorProps: CompanySelectorProps = {
    ...panelSelectorProps,
    companiesListOptions,
    activeCompanyId,
    onCompanyMouseEnter: handleCompanyMouseEnter,
    onCompanyPanelSelectedChange: findAndSelectPanel,
  };

  return {
    isCompaniesLoading,
    isSingleCompany,
    panelSelectorProps,
    companySelectorProps,
    setPendingCompanyId,
  };
};
